import React, { useEffect, useState } from "react";
import {
  Trash2,
  Upload,
  Plus,
  Edit3,
  X,
  UserRound,
  Eye,
  EyeOff,
} from "lucide-react";
import { toast } from "react-toastify";
import { http } from "../../../axios/axios";

const initialForm = {
  name: "",
  role: "",
  bio: "",
  focus: "",
  imagePosition: "center center",
  order: 0,
  isActive: true,
};

export default function PortfolioManager() {
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState("");

  const [isEditing, setIsEditing] = useState(null);

  const [formData, setFormData] = useState(initialForm);

  // ================================
  // FETCH PORTFOLIOS
  // ================================

  const fetchPortfolios = async () => {
    try {
      setLoading(true);

      const res = await http.get("/admin/portfolio");

      console.log("Portfolio response:", res.data);

      setPortfolios(res.data?.data || []);
    } catch (err) {
      console.error(
        "Portfolio fetch error:",
        err.response?.data || err.message
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPortfolios();
  }, []);

  // ================================
  // IMAGE CHANGE
  // ================================

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setImageFile(file);

    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }

    setPreview(URL.createObjectURL(file));
  };

  // ================================
  // RESET FORM
  // ================================

  const resetForm = () => {
    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }

    setFormData(initialForm);
    setImageFile(null);
    setPreview("");
    setIsEditing(null);
  };

  // ================================
  // INPUT CHANGE
  // ================================

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // ================================
  // CREATE / UPDATE
  // ================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Please enter name");
    }

    if (!formData.role.trim()) {
      toast.error("Please enter role");
    }

    if (!formData.bio.trim()) {
      toast.error("Please enter bio");
    }

    if (!isEditing && !imageFile) {
      toast.error("Please upload portfolio image");
      return;
    }

    const data = new FormData();

    data.append("name", formData.name);
    data.append("role", formData.role);
    data.append("bio", formData.bio);
    data.append("focus", formData.focus);
    data.append("imagePosition", formData.imagePosition);
    data.append("order", formData.order);
    data.append("isActive", String(formData.isActive));

    if (imageFile) {
      data.append("image", imageFile);
    }

    try {
      setSubmitting(true);

      if (isEditing) {
        await http.put(`/admin/portfolio/${isEditing}`, data);
      } else {
        await http.post("/admin/portfolio", data);
      }

      resetForm();

      await fetchPortfolios();

      toast.success(
        isEditing
          ? "Portfolio updated successfully"
          : "Portfolio created successfully"
      );
    } catch (err) {
      console.error(
        "Portfolio operation error:",
        err.response?.data || err.message
      );

      toast.error(
        err.response?.data?.message ||
          "Portfolio operation failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ================================
  // EDIT
  // ================================

  const handleEdit = (portfolio) => {
    setIsEditing(portfolio._id);

    setFormData({
      name: portfolio.name || "",
      role: portfolio.role || "",
      bio: portfolio.bio || "",
      focus: portfolio.focus || "",
      imagePosition: portfolio.imagePosition || "center center",
      order: portfolio.order ?? 0,
      isActive: portfolio.isActive ?? true,
    });

    // Cloudinary URL can be used directly
    setPreview(portfolio.image || "");

    // Do NOT set existing image as file
    setImageFile(null);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // ================================
  // DELETE
  // ================================

  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this portfolio member?"
    );

    if (!confirmed) return;

    try {
      await http.delete(`/admin/portfolio/${id}`);

      if (isEditing === id) {
        resetForm();
      }

      await fetchPortfolios();
    } catch (err) {
      console.error(
        "Portfolio delete error:",
        err.response?.data || err.message
      );

      toast.error(
        err.response?.data?.message ||
          "Delete failed"
      );
    }
  };

  // ================================
  // ACTIVE / INACTIVE
  // ================================

  const handleStatusToggle = async (portfolio) => {
    try {
      const data = new FormData();

      data.append(
        "isActive",
        String(!portfolio.isActive)
      );

      await http.put(
        `/admin/portfolio/${portfolio._id}`,
        data
      );

      await fetchPortfolios();
    } catch (err) {
      console.error(
        "Status update error:",
        err.response?.data || err.message
      );

      toast.error("Could not change portfolio status");
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] p-4 font-sans text-white md:p-8">
      <div className="mx-auto max-w-7xl">

        {/* HEADER */}

        <div className="mb-8 flex items-center justify-between">
          <h2 className="flex items-center gap-3 text-2xl font-black uppercase tracking-tighter md:text-3xl">
            <div className="h-8 w-2 rounded-full bg-blue-600" />

            {isEditing
              ? "Update Portfolio"
              : "Add Portfolio Member"}
          </h2>

          {isEditing && (
            <button
              type="button"
              onClick={resetForm}
              className="flex items-center gap-2 text-[10px] font-black uppercase text-red-500 transition-colors hover:text-white"
            >
              <X size={16} />

              Cancel Edit
            </button>
          )}
        </div>

        {/* ============================= */}
        {/* FORM */}
        {/* ============================= */}

        <form
          onSubmit={handleSubmit}
          className={`mb-12 rounded-[2rem] border bg-[#141414] p-5 shadow-2xl transition-all duration-500 md:p-8 ${
            isEditing
              ? "border-blue-600/40"
              : "border-white/5"
          }`}
        >
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">

            {/* LEFT */}

            <div className="space-y-5">

              {/* NAME */}

              <div className="space-y-2">
                <label className="ml-1 text-[10px] font-black uppercase text-slate-500">
                  Name *
                </label>

                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter member name"
                  className="w-full rounded-xl border border-white/10 bg-[#1c1c1c] p-4 text-xs font-bold outline-none transition-all focus:border-blue-600"
                />
              </div>

              {/* ROLE */}

              <div className="space-y-2">
                <label className="ml-1 text-[10px] font-black uppercase text-slate-500">
                  Role *
                </label>

                <input
                  type="text"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  placeholder="Founder – Suraksha 24/7"
                  className="w-full rounded-xl border border-white/10 bg-[#1c1c1c] p-4 text-xs font-bold outline-none focus:border-blue-600"
                />
              </div>

              {/* FOCUS */}

              <div className="space-y-2">
                <label className="ml-1 text-[10px] font-black uppercase text-slate-500">
                  Focus
                </label>

                <input
                  type="text"
                  name="focus"
                  value={formData.focus}
                  onChange={handleChange}
                  placeholder="Technology • Security • Business Automation"
                  className="w-full rounded-xl border border-white/10 bg-[#1c1c1c] p-4 text-xs font-bold outline-none focus:border-blue-600"
                />
              </div>

              {/* IMAGE POSITION */}

              <div className="space-y-2">
                <label className="ml-1 text-[10px] font-black uppercase text-slate-500">
                  Image Position
                </label>

                <select
                  name="imagePosition"
                  value={formData.imagePosition}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-white/10 bg-[#1c1c1c] p-4 text-xs font-bold outline-none focus:border-blue-600"
                >
                  <option value="center center">
                    Center Center
                  </option>

                  <option value="center 20%">
                    Center 20%
                  </option>

                  <option value="center 40%">
                    Center 40%
                  </option>

                  <option value="center 60%">
                    Center 60%
                  </option>

                  <option value="top center">
                    Top Center
                  </option>
                </select>
              </div>

              {/* ORDER */}

              <div className="space-y-2">
                <label className="ml-1 text-[10px] font-black uppercase text-slate-500">
                  Display Order
                </label>

                <input
                  type="number"
                  name="order"
                  min="0"
                  value={formData.order}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-white/10 bg-[#1c1c1c] p-4 text-xs font-bold outline-none focus:border-blue-600"
                />
              </div>

              {/* ACTIVE */}

              <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-white/10 bg-[#1c1c1c] p-4">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  className="h-4 w-4"
                />

                <span className="text-[10px] font-black uppercase">
                  Portfolio Active
                </span>
              </label>
            </div>

            {/* RIGHT */}

            <div className="space-y-6">

              {/* BIO */}

              <div className="space-y-2">
                <label className="ml-1 text-[10px] font-black uppercase text-slate-500">
                  Biography *
                </label>

                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  placeholder="Enter portfolio member biography..."
                  className="h-44 w-full resize-none rounded-xl border border-white/10 bg-[#1c1c1c] p-4 text-xs font-medium leading-6 outline-none focus:border-blue-600"
                />
              </div>

              {/* IMAGE */}

              <div className="relative">
                <label className="mb-2 ml-1 block text-[10px] font-black uppercase text-slate-500">
                  Portfolio Image
                  {!isEditing && " *"}
                </label>

                <div className="relative flex min-h-[280px] items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-white/10 bg-[#0d0d0d] transition-all hover:border-white/20">

                  {preview ? (
                    <img
                      src={preview}
                      alt="Portfolio Preview"
                      style={{
                        objectPosition:
                          formData.imagePosition ||
                          "center center",
                      }}
                      className="h-[280px] w-full object-cover"
                    />
                  ) : (
                    <div className="text-center">
                      <Upload
                        className="mx-auto mb-2 text-slate-700"
                        size={36}
                      />

                      <p className="text-[9px] font-black uppercase tracking-wider text-slate-600">
                        Click to upload portfolio image
                      </p>
                    </div>
                  )}

                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleFileChange}
                    className="absolute inset-0 cursor-pointer opacity-0"
                  />
                </div>

                {isEditing && (
                  <p className="mt-2 text-[9px] font-bold text-slate-600">
                    Leave the image unchanged if you don't
                    want to replace it.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* SUBMIT */}

          <button
            type="submit"
            disabled={submitting}
            className="mt-10 flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 p-5 text-[11px] font-black uppercase tracking-widest text-white shadow-lg transition-all hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isEditing ? (
              <Edit3 size={18} />
            ) : (
              <Plus size={18} />
            )}

            {submitting
              ? "Please Wait..."
              : isEditing
                ? "Update Portfolio Member"
                : "Add Portfolio Member"}
          </button>
        </form>

        {/* ============================= */}
        {/* PORTFOLIO LIST */}
        {/* ============================= */}

        <h3 className="mb-6 flex items-center gap-2 text-xs font-black uppercase tracking-[0.3em] text-slate-500">
          <UserRound size={14} />

          Portfolio Members ({portfolios.length})
        </h3>

        {loading ? (
          <div className="py-20 text-center text-xs font-black uppercase text-slate-500">
            Loading Portfolios...
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">

            {portfolios.length === 0 ? (
              <div className="col-span-full rounded-[2rem] border-2 border-dashed border-white/5 py-20 text-center">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-600">
                  No portfolio members found.
                </p>
              </div>
            ) : (
              portfolios.map((item) => (
                <div
                  key={item._id}
                  className="group overflow-hidden rounded-[2rem] border border-white/5 bg-[#141414] shadow-xl transition-all hover:border-blue-600/30"
                >

                  {/* IMAGE */}

                  <div className="relative h-[340px] overflow-hidden">

                    <img
                      src={item.image}
                      alt={item.name}
                      style={{
                        objectPosition:
                          item.imagePosition ||
                          "center center",
                      }}
                      className="h-full w-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105"
                    />

                    {/* STATUS */}

                    <div className="absolute left-4 top-4">
                      <span
                        className={`rounded-full px-3 py-1 text-[9px] font-black uppercase ${
                          item.isActive
                            ? "bg-green-500 text-white"
                            : "bg-red-500 text-white"
                        }`}
                      >
                        {item.isActive
                          ? "Active"
                          : "Inactive"}
                      </span>
                    </div>

                    {/* ACTIONS */}

                    <div className="absolute inset-0 flex items-center justify-center gap-4 bg-black/60 opacity-0 transition-opacity group-hover:opacity-100">

                      <button
                        type="button"
                        onClick={() =>
                          handleEdit(item)
                        }
                        className="rounded-full bg-blue-600 p-3 transition-transform hover:scale-110"
                        title="Edit"
                      >
                        <Edit3 size={18} />
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          handleStatusToggle(item)
                        }
                        className={`rounded-full p-3 transition-transform hover:scale-110 ${
                          item.isActive
                            ? "bg-orange-500"
                            : "bg-green-600"
                        }`}
                        title={
                          item.isActive
                            ? "Deactivate"
                            : "Activate"
                        }
                      >
                        {item.isActive ? (
                          <EyeOff size={18} />
                        ) : (
                          <Eye size={18} />
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          handleDelete(item._id)
                        }
                        className="rounded-full bg-red-600 p-3 transition-transform hover:scale-110"
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>

                  {/* DETAILS */}

                  <div className="p-6">

                    <div className="mb-1 flex items-center justify-between gap-3">

                      <h3 className="truncate text-sm font-black uppercase tracking-tight text-white">
                        {item.name}
                      </h3>

                      <span className="rounded bg-white/5 px-2 py-1 text-[9px] font-black text-slate-400">
                        #{item.order}
                      </span>
                    </div>

                    <p className="mb-3 text-[10px] font-bold uppercase text-blue-500">
                      {item.role}
                    </p>

                    {item.focus && (
                      <p className="mb-3 text-[9px] font-bold text-slate-500">
                        {item.focus}
                      </p>
                    )}

                    <p
                      className="overflow-hidden text-xs leading-5 text-slate-500"
                      style={{
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 3,
                      }}
                    >
                      {item.bio}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}